import { type DateSchema, type NumberSchema, type StringSchema } from 'yup';
import * as yup from 'yup';

import {
  ALPHA_NUMERIC_MESSAGE,
  ALPHA_NUMERIC_SPACE_MESSAGE,
  EMAIL_MESSAGE,
  LENGTH_MESSAGE,
  MAX_DATE_MESSAGE,
  MAX_MESSAGE,
  MAX_NUMBER_MESSAGE,
  MIN_DATE_MESSAGE,
  MIN_MESSAGE,
  MIN_NUMBER_MESSAGE,
  NUMERIC_MESSAGE,
  REQUIRED_MESSAGE,
} from '../../constants/validationMessages';
import { ALPHA_NUMERIC_REGEX, ALPHA_NUMERIC_SPACE_REGEX } from '../../../../constants/regex';
import { type TValidationConfig } from '../../types/configs';

import { getFieldMandatoryConfigValidation } from '../../fieldConfig';
import getValidationsFromConfig from '../getValidationsFromConfig';
import { formatDate } from '../../../../helpers/dateHelpers';
import i18n from '../../../../configs/i18n';

interface IGetValidationArgs {
  field: string;
  operation?: string;
  defaultRequired?: boolean;
  validationConfig?: TValidationConfig;
}

interface IGetStringValidationArgs extends IGetValidationArgs {
  alphanumericSpace?: boolean;
  alphanumeric?: boolean;
  min?: number;
  max?: number;
  length?: number;
  email?: boolean;
}

export const getStringValidation = ({
  field,
  alphanumeric,
  alphanumericSpace,
  max,
  min,
  length,
  email,
  operation,
  defaultRequired = false,
  validationConfig,
}: IGetStringValidationArgs) => {
  let schema = yup
    .string()
    .nullable()
    .transform((_, value: string) => value?.trim() || null);

  if (validationConfig) {
    schema = getValidationsFromConfig(validationConfig, field, schema) as StringSchema;
  }

  if (alphanumeric) {
    schema = schema.matches(ALPHA_NUMERIC_REGEX, () => i18n.t(ALPHA_NUMERIC_MESSAGE) as string);
  }

  if (alphanumericSpace) {
    schema = schema.matches(ALPHA_NUMERIC_SPACE_REGEX, () => i18n.t(ALPHA_NUMERIC_SPACE_MESSAGE) as string);
  }

  if (length) {
    schema = schema.length(length, () => i18n.t(LENGTH_MESSAGE, { length }) as string);
  }

  if (max) {
    schema = schema.max(max, () => i18n.t(MAX_MESSAGE, { max }) as string);
  }

  if (min != null) {
    schema = schema.min(min, () => i18n.t(MIN_MESSAGE, { min }) as string);
  }

  if (email) {
    schema = schema.email(() => i18n.t(EMAIL_MESSAGE) as string);
  }

  return schema
    .typeError(() => i18n.t(REQUIRED_MESSAGE) as string)
    .notRequired()
    .when('selectedOperation', {
      is: (selectedOperation: string) => {
        if (!operation && !selectedOperation) {
          return defaultRequired;
        }

        return getFieldMandatoryConfigValidation(field)(operation || selectedOperation);
      },
      then: (schema) =>
        schema.required(() => i18n.t(REQUIRED_MESSAGE) as string).typeError(() => i18n.t(REQUIRED_MESSAGE) as string),
    });
};

interface IGetNumberValidationArgs extends IGetValidationArgs {
  integer?: boolean;
  min?: number;
  max?: number;
}

export const getNumberValidation = ({
  field,
  max,
  min,
  operation,
  defaultRequired,
  validationConfig,
}: IGetNumberValidationArgs) => {
  let schema = yup
    .number()
    .nullable()
    .transform((value: number) => (isNaN(value) ? null : value));

  if (max) {
    schema = schema.max(max, () => i18n.t(MAX_NUMBER_MESSAGE, { max }) as string);
  }

  if (min != null) {
    schema = schema.min(min, () => i18n.t(MIN_NUMBER_MESSAGE, { min }) as string);
  }

  if (validationConfig) {
    schema = getValidationsFromConfig(validationConfig, field, schema) as NumberSchema;
  }

  return schema.notRequired().when('selectedOperation', {
    is: (selectedOperation: string) => {
      if (!operation && !selectedOperation) {
        return defaultRequired;
      }

      return getFieldMandatoryConfigValidation(field)(operation || selectedOperation);
    },
    then: (schema) =>
      schema
        .required(() => i18n.t(REQUIRED_MESSAGE) as string)
        .typeError(() => `${i18n.t(field) as string} ${i18n.t(NUMERIC_MESSAGE) as string}`),
  });
};

type IGetBooleanValidationArgs = IGetValidationArgs;

export const getBooleanValidation = ({ field, operation, defaultRequired }: IGetBooleanValidationArgs) => {
  const schema = yup.boolean().transform((value: boolean, originalValue, context) => {
    if (context.isType(value)) {
      return value;
    }

    return false;
  });

  return schema
    .typeError(() => i18n.t(REQUIRED_MESSAGE) as string)
    .notRequired()
    .when('selectedOperation', {
      is: (selectedOperation: string) => {
        if (!operation && !selectedOperation) {
          return defaultRequired;
        }

        return getFieldMandatoryConfigValidation(field)(operation || selectedOperation);
      },
      then: (schema) =>
        schema.required(() => i18n.t(REQUIRED_MESSAGE) as string).typeError(() => i18n.t(REQUIRED_MESSAGE) as string),
    });
};

interface IGetDateValidationArgs extends IGetValidationArgs {
  min?: string | Date;
  max?: string | Date;
}

export const getDateValidation = ({
  field,
  max,
  min,
  operation,
  defaultRequired = false,
  validationConfig,
}: IGetDateValidationArgs) => {
  let schema = yup.date().transform((value, originalValue, context) => {
    if (context.isType(value)) {
      return value;
    }

    return undefined;
  });

  if (max) {
    schema = schema.max(max, () => i18n.t(MAX_DATE_MESSAGE, { max: formatDate(max) }) as string);
  }

  if (min) {
    schema = schema.min(min, () => i18n.t(MIN_DATE_MESSAGE, { min: formatDate(min) }) as string);
  }

  if (validationConfig) {
    schema = getValidationsFromConfig(validationConfig, field, schema) as DateSchema;
  }

  return schema
    .typeError(() => i18n.t(REQUIRED_MESSAGE) as string)
    .notRequired()
    .when('selectedOperation', {
      is: (selectedOperation: string) => {
        if (!operation && !selectedOperation) {
          return defaultRequired;
        }

        return getFieldMandatoryConfigValidation(field)(operation || selectedOperation);
      },
      then: (schema) =>
        schema.required(() => i18n.t(REQUIRED_MESSAGE) as string).typeError(() => i18n.t(REQUIRED_MESSAGE) as string),
    });
};

type IGetObjectValidationArgs = IGetValidationArgs;

export const getObjectValidation = ({ field, operation, defaultRequired = false }: IGetObjectValidationArgs) => {
  const schema = yup
    .object()
    .typeError(() => i18n.t(REQUIRED_MESSAGE) as string)
    .notRequired();

  return schema.when('selectedOperation', {
    is: (selectedOperation: string) => {
      if (!operation && !selectedOperation) {
        return defaultRequired;
      }

      return getFieldMandatoryConfigValidation(field)(operation || selectedOperation);
    },
    then: (schema) =>
      schema.required(() => i18n.t(REQUIRED_MESSAGE) as string).typeError(() => i18n.t(REQUIRED_MESSAGE) as string),
    otherwise: (schema) => schema.notRequired(),
  });
};

export const addFieldConfigValidation =
  (schema: yup.MixedSchema) =>
  ({ field, operation, defaultRequired = false }: IGetObjectValidationArgs) =>
    schema
      .typeError(() => i18n.t(REQUIRED_MESSAGE) as string)
      .notRequired()
      .when('selectedOperation', {
        is: (selectedOperation: string) => {
          if (!operation && !selectedOperation) {
            return defaultRequired;
          }

          return getFieldMandatoryConfigValidation(field)(operation || selectedOperation);
        },
        then: (schema) =>
          schema.required(() => i18n.t(REQUIRED_MESSAGE) as string).typeError(() => i18n.t(REQUIRED_MESSAGE) as string),
      });
